import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { isPlainObject } from 'lodash'

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: 'application/json',
  'Content-Type': 'application/json; charset=utf-8',
  'X-Requested-With': 'XMLHttpRequest'
}
class Http {
  private instance: AxiosInstance | null = null
  private instanceWithoutCreds: AxiosInstance | null = null

  public get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp(true)
  }

  private get httpWithoutCreds(): AxiosInstance {
    return this.instanceWithoutCreds != null ? this.instanceWithoutCreds : this.initHttp(false)
  }

  // Utility function to convert nested objects to a URL-encoded JSON string
  private jsonStringSerializer = (params: any): string => {
    const parts: string[] = []

    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key]

        // If the value is a complex object/array (like 'filters'),
        // convert it to a JSON string and URL-encode the whole thing.
        if (typeof value === 'object' && value !== null && (isPlainObject(value) || Array.isArray(value))) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`)
        } else if (value !== undefined && value !== null) {
          // For simple values (limit, skip, query, sort), use standard serialization
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        }
      }
    }
    return parts.join('&')
  }

  initHttp(credentials: boolean) {
    const http = axios.create({
      baseURL: `${process.env.VUE_APP_API_URI || 'http://localhost:3000'}/api`,
      headers,
      withCredentials: credentials,
      paramsSerializer: this.jsonStringSerializer
    })

    http.interceptors.response.use(
      response => response,
      error => {
        const { response } = error
        return this.handleError(response)
      }
    )

    if (credentials) this.instance = http
    else this.instanceWithoutCreds = http
    return http
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
    return this.http.request({ ...config })
  }

  get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.http.get<T, R>(url, { ...config })
  }

  post<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.http.post<T, R>(url, data, { ...config })
  }

  put<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.http.put<T, R>(url, data, { ...config })
  }

  patch<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.http.patch<T, R>(url, data, { ...config })
  }

  delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.http.delete<T, R>(url, { ...config })
  }

  putWithoutCreds<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.httpWithoutCreds.put<T, R>(url, data, config)
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // Handle global app errors
  private handleError(error: { status: number }) {
    console.error(error)

    if (error?.status) {
      const { status } = error

      switch (status) {
        case StatusCode.InternalServerError: {
          // Handle InternalServerError
          break
        }
        case StatusCode.Forbidden: {
          // Redirect to login if we get a forbidden response
          window.location.href = process.env.VUE_APP_CLIENT_URI || 'http://localhost:4200'
          break
        }
        case StatusCode.Unauthorized: {
          // Handle Unauthorized
          break
        }
        case StatusCode.TooManyRequests: {
          // Handle TooManyRequests
          break
        }
      }
    }

    return Promise.reject(error)
  }
}

export const http = new Http()
